/**
 * Internal Notes store (FEAT-V2-009, Bloco C).
 *
 * Hooks React Query pra:
 * - useInternalNotes(cardId, workflow) — lista notas
 * - useInternalNotesActions() — add/edit/delete
 *
 * Permissão é enforced no backend (canAccessDeptOfWorkflow). UI usa
 * `useCanSeeInternalNotes(workflowSlug)` pra decidir se exibe a aba —
 * evita request 403 desnecessário.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';
import { useAuth } from '@/contexts/AuthContext';

const NOTE_EDIT_WINDOW_MS = 5 * 60 * 1000; // espelha backend

// ─── Tipos ──────────────────────────────────────────────────────────────

export interface InternalNote {
  id: string;
  cardId: string;
  workflowSlug: string;
  autorId: string;
  body: string;
  criadoEmIso: string;
  editadoEmIso: string | null;
  deletadoEmIso: string | null;
  deletadoPorId: string | null;
}

export interface CreateInternalNoteInput {
  workflowSlug: string;
  body: string;
}

// ─── Keys ───────────────────────────────────────────────────────────────

export const INTERNAL_NOTES_KEYS = {
  list: (cardId: string, workflowSlug: string) =>
    ['v1', 'cards', cardId, 'internal-notes', { workflow: workflowSlug }] as const,
};

// ─── Visibility helper ──────────────────────────────────────────────────

/**
 * Verifica se o user atual pode VER a aba 🔒 (independente do backend).
 * Admin sempre pode. Não-admin precisa do dept_slug do workflow no
 * `user.departments`.
 *
 * O parâmetro `workflowSlug` é tratado igual no backend:
 *   - Pode ser slug do workflow (resolve dept indiretamente)
 *   - Ou pode ser slug do dept direto (pattern legacy cards.ts)
 *
 * Sem acesso a `workflows` table no frontend, fazemos checagem otimista:
 * incluímos workflowSlug DIRETO em user.departments. Se for slug de
 * workflow (não dept), o backend ainda valida via canAccessDeptOfWorkflow
 * em 2 passos — UI pode ser falso-negativo mas nunca falso-positivo
 * (defesa em profundidade).
 */
export function useCanSeeInternalNotes(workflowSlug: string): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.isAdmin) return true;
  return user.departments.includes(workflowSlug);
}

// ─── Queries ────────────────────────────────────────────────────────────

export function useInternalNotes(
  cardId: string | null,
  workflowSlug: string | null
) {
  return useQuery({
    queryKey: INTERNAL_NOTES_KEYS.list(cardId ?? '', workflowSlug ?? ''),
    queryFn: () =>
      api.get<InternalNote[]>(
        `/api/v1/cards/${encodeURIComponent(cardId!)}/internal-notes?workflow=${encodeURIComponent(workflowSlug!)}`
      ),
    enabled: !!cardId && !!workflowSlug && hasToken(),
    staleTime: 30_000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────

export function useInternalNotesActions(cardId: string, workflowSlug: string) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: INTERNAL_NOTES_KEYS.list(cardId, workflowSlug) });
  }, [qc, cardId, workflowSlug]);

  const addNote = useCallback(
    async (body: string): Promise<InternalNote> => {
      setPending('add');
      try {
        const r = await api.post<InternalNote>(
          `/api/v1/cards/${encodeURIComponent(cardId)}/internal-notes`,
          { workflowSlug, body }
        );
        invalidate();
        return r;
      } finally {
        setPending(null);
      }
    },
    [cardId, workflowSlug, invalidate]
  );

  const editNote = useCallback(
    async (noteId: string, body: string): Promise<InternalNote> => {
      setPending(noteId);
      try {
        const r = await api.patch<InternalNote>(
          `/api/v1/internal-notes/${encodeURIComponent(noteId)}`,
          { body }
        );
        invalidate();
        return r;
      } finally {
        setPending(null);
      }
    },
    [invalidate]
  );

  const deleteNote = useCallback(
    async (noteId: string): Promise<void> => {
      setPending(noteId);
      try {
        await api.delete(`/api/v1/internal-notes/${encodeURIComponent(noteId)}`);
        invalidate();
      } finally {
        setPending(null);
      }
    },
    [invalidate]
  );

  return useMemo(
    () => ({ addNote, editNote, deleteNote, pending }),
    [addNote, editNote, deleteNote, pending]
  );
}

// ─── Helper: pode editar (janela 5min + autor) ──────────────────────────

export function canEditInternalNote(note: InternalNote, currentUserId: string): boolean {
  if (note.autorId !== currentUserId) return false;
  if (note.deletadoEmIso) return false;
  const createdAtMs = Date.parse(
    note.criadoEmIso + (note.criadoEmIso.endsWith('Z') ? '' : 'Z')
  );
  return Date.now() - createdAtMs <= NOTE_EDIT_WINDOW_MS;
}
